import { useEffect, useMemo, useState } from 'react';
import { Shield, Search, CheckCircle2, XCircle, Activity, Loader2 } from 'lucide-react';
import { fetchLoginLogs, LoginLog } from '../services/loginLogService';

type StatusFilter = 'all' | 'success' | 'failure';

function formatDateTime(d: Date): string {
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function shortDevice(ua: string): string {
  if (!ua) return '—';
  // Petite heuristique pour rester lisible
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Android/i.test(ua)) return 'Android';
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'Mac';
  if (/Linux/i.test(ua)) return 'Linux';
  return ua.slice(0, 40);
}

export default function LoginLogsPage() {
  const [logs, setLogs] = useState<LoginLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchLoginLogs(1000);
        if (!cancelled) setLogs(data);
      } catch (e) {
        console.error('fetchLoginLogs error', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const counts = useMemo(() => {
    const total = logs.length;
    const success = logs.filter((l) => l.success).length;
    return { total, success, failure: total - success };
  }, [logs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((l) => {
      if (status === 'success' && !l.success) return false;
      if (status === 'failure' && l.success) return false;
      if (!q) return true;
      return (
        (l.userName ?? '').toLowerCase().includes(q) ||
        (l.phone ?? '').toLowerCase().includes(q)
      );
    });
  }, [logs, status, search]);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-[#00665C]/10 text-[#00665C]">
          <Shield size={22} />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
            Traçabilité des connexions
          </h1>
          <p className="text-sm text-gray-500">
            Historique des tentatives de connexion (succès et échecs)
          </p>
        </div>
      </div>

      {/* Compteurs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#00665C]/10 text-[#00665C]">
            <Activity size={20} />
          </div>
          <div>
            <div className="text-xs text-gray-500">Total</div>
            <div className="text-2xl font-bold text-gray-800">{counts.total}</div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
            <CheckCircle2 size={20} />
          </div>
          <div>
            <div className="text-xs text-gray-500">Réussies</div>
            <div className="text-2xl font-bold text-emerald-600">{counts.success}</div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-red-50 text-red-600">
            <XCircle size={20} />
          </div>
          <div>
            <div className="text-xs text-gray-500">Échouées</div>
            <div className="text-2xl font-bold text-red-600">{counts.failure}</div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-4 mb-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {(
            [
              { key: 'all', label: 'Toutes' },
              { key: 'success', label: 'Réussies' },
              { key: 'failure', label: 'Échouées' },
            ] as { key: StatusFilter; label: string }[]
          ).map((opt) => (
            <button
              key={opt.key}
              onClick={() => setStatus(opt.key)}
              className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                status === opt.key
                  ? 'bg-[#00665C] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="relative w-full sm:w-72">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (nom ou téléphone)"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:border-[#00665C]"
          />
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-gray-500">
            <Loader2 className="animate-spin mr-2" size={18} />
            Chargement...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-500 text-sm">
            Aucune tentative de connexion à afficher.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Statut</th>
                  <th className="text-left px-4 py-3">Utilisateur</th>
                  <th className="text-left px-4 py-3">Téléphone</th>
                  <th className="text-left px-4 py-3">Rôle</th>
                  <th className="text-left px-4 py-3">Appareil</th>
                  <th className="text-left px-4 py-3">Date / Heure</th>
                  <th className="text-left px-4 py-3">Raison d'échec</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      {l.success ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                          <CheckCircle2 size={12} /> Succès
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">
                          <XCircle size={12} /> Échec
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-800 font-medium">
                      {l.userName ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{l.phone ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600 capitalize">
                      {l.userRole ?? '—'}
                    </td>
                    <td
                      className="px-4 py-3 text-gray-600 max-w-[180px] truncate"
                      title={l.device}
                    >
                      {shortDevice(l.device)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {formatDateTime(l.timestamp)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {l.success ? '—' : l.failureReason ?? 'Non précisé'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
